import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styles from './CourseSelector.module.css';
import { useStores } from '../../../../hooks/useStore';
import { CourseModel } from '../../../../models/coursesAndPersons';
import { CreateCourseRequest } from '../../../../api/reviews/dto/CoursesAndPersonsRequest.dto';
import { Button } from '../../../../components/UI/Button/Button';
import { Modal } from '../../../../components/UI/Modal/Modal';

interface CourseSelectorProps {
  value?: string;
  onChange: (selectedCourseId: string) => void;
  placeholder?: string;
}

export const CourseSelector: React.FC<CourseSelectorProps> = observer(
  ({ value, onChange, placeholder = 'Выберите курс' }) => {
    const { userStore } = useStores();
    const [courses, setCourses] = useState<CourseModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editingCourse, setEditingCourse] = useState<CourseModel | null>(
      null,
    );
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [newCourse, setNewCourse] = useState<CreateCourseRequest>({
      translations: [
        {
          locale: 'ru',
          name: '',
          slug_suffix: '',
        },
        {
          locale: 'en',
          name: '',
          slug_suffix: '',
        },
      ],
    });

    useEffect(() => {
      loadCourses();
    }, []);

    const loadCourses = async () => {
      setLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.getCourses();
        if (response.success && response.body) {
          setCourses(response.body.courses);
        }
      } catch (error) {
        // Ошибка загрузки курсов
      } finally {
        setLoading(false);
      }
    };

    const handleCreateCourse = async () => {
      if (!newCourse.translations[0].name || !newCourse.translations[1].name) {
        return;
      }

      setCreateLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.createCourse(newCourse);
        if (response.success && response.body) {
          const createdCourse = response.body;
          setCourses((prev) => [...prev, createdCourse]);
          onChange(createdCourse.id);
          setShowCreateModal(false);
          setNewCourse({
            translations: [
              { locale: 'ru', name: '', slug_suffix: '' },
              { locale: 'en', name: '', slug_suffix: '' },
            ],
          });
        }
      } catch (error) {
        // Ошибка создания курса
      } finally {
        setCreateLoading(false);
      }
    };

    const handleEditCourse = async () => {
      if (
        !editingCourse ||
        !editingCourse.translations[0].name ||
        !editingCourse.translations[1].name
      ) {
        return;
      }

      setEditLoading(true);
      try {
        const updateData = {
          translations: editingCourse.translations.map((t) => ({
            locale: t.locale,
            name: t.name,
            slug_suffix: t.slug_suffix,
          })),
        };

        const response =
          await userStore.mainStore.apiClient.reviews.updateCourse(
            editingCourse.id,
            updateData,
          );
        if (response.success && response.body) {
          const updatedCourse = response.body;
          setCourses((prev) =>
            prev.map((c) => (c.id === updatedCourse.id ? updatedCourse : c)),
          );
          setEditingCourse(null);
        }
      } catch (error) {
        // Ошибка редактирования курса
      } finally {
        setEditLoading(false);
      }
    };

    const handleDeleteCourse = async (courseId: string) => {
      if (!window.confirm('Вы уверены, что хотите удалить этот курс?')) {
        return;
      }

      setDeleteLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.deleteCourse(courseId);
        if (response.success) {
          setCourses((prev) => prev.filter((c) => c.id !== courseId));
          if (value === courseId) {
            onChange('');
          }
        }
      } catch (error) {
        // Ошибка удаления курса
      } finally {
        setDeleteLoading(false);
      }
    };

    const startEditCourse = (course: CourseModel) => {
      setEditingCourse({ ...course });
    };

    const getCourseName = (course: CourseModel, locale: 'ru' | 'en' = 'ru') => {
      const translation = course.translations.find((t) => t.locale === locale);
      return translation?.name || course.translations[0]?.name || course.id;
    };

    const selectedCourse = courses.find((c) => c.id === value);

    return (
      <div className={styles.courseSelector}>
        <div className={styles.selectRow}>
          <select
            className={styles.select}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={loading}
          >
            <option value="">{loading ? 'Загрузка...' : placeholder}</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {getCourseName(course)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            appearance="link"
            onClick={() => setShowCreateModal(true)}
            className={styles.addButton}
            title="Добавить курс"
          >
            +
          </Button>
        </div>

        {selectedCourse && (
          <div className={styles.selectedInfo}>
            <div className={styles.courseInfo}>
              <small>
                RU: {getCourseName(selectedCourse, 'ru')} | EN:{' '}
                {getCourseName(selectedCourse, 'en')}
              </small>
            </div>
            <div className={styles.courseActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => startEditCourse(selectedCourse)}
                className={styles.actionButton}
              >
                ✏️ Редактировать
              </Button>
              <Button
                type="button"
                appearance="link"
                onClick={() => handleDeleteCourse(selectedCourse.id)}
                disabled={deleteLoading}
                className={styles.deleteButton}
              >
                {deleteLoading ? '⏳' : '🗑️'} Удалить
              </Button>
            </div>
          </div>
        )}

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Создать новый курс"
        >
          <div className={styles.createForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Название курса (RU)*</label>
              <input
                type="text"
                className={styles.input}
                value={newCourse.translations[0].name}
                onChange={(e) =>
                  setNewCourse((prev) => ({
                    ...prev,
                    translations: prev.translations.map((t, i) =>
                      i === 0 ? { ...t, name: e.target.value } : t,
                    ),
                  }))
                }
                placeholder="Введите название курса на русском"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Course Name (EN)*</label>
              <input
                type="text"
                className={styles.input}
                value={newCourse.translations[1].name}
                onChange={(e) =>
                  setNewCourse((prev) => ({
                    ...prev,
                    translations: prev.translations.map((t, i) =>
                      i === 1 ? { ...t, name: e.target.value } : t,
                    ),
                  }))
                }
                placeholder="Enter course name in English"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Slug суффикс (RU)</label>
              <input
                type="text"
                className={styles.input}
                value={newCourse.translations[0].slug_suffix}
                onChange={(e) =>
                  setNewCourse((prev) => ({
                    ...prev,
                    translations: prev.translations.map((t, i) =>
                      i === 0 ? { ...t, slug_suffix: e.target.value } : t,
                    ),
                  }))
                }
                placeholder="course-name-ru"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Slug suffix (EN)</label>
              <input
                type="text"
                className={styles.input}
                value={newCourse.translations[1].slug_suffix}
                onChange={(e) =>
                  setNewCourse((prev) => ({
                    ...prev,
                    translations: prev.translations.map((t, i) =>
                      i === 1 ? { ...t, slug_suffix: e.target.value } : t,
                    ),
                  }))
                }
                placeholder="course-name-en"
              />
            </div>

            <div className={styles.modalActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => setShowCreateModal(false)}
              >
                Отмена
              </Button>
              <Button
                type="button"
                appearance="auth"
                onClick={handleCreateCourse}
                disabled={
                  !newCourse.translations[0].name ||
                  !newCourse.translations[1].name ||
                  createLoading
                }
              >
                {createLoading ? 'Создание...' : 'Создать курс'}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={!!editingCourse}
          onClose={() => setEditingCourse(null)}
          title="Редактировать курс"
        >
          {editingCourse && (
            <div className={styles.createForm}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название курса (RU)*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    editingCourse.translations.find((t) => t.locale === 'ru')
                      ?.name || ''
                  }
                  onChange={(e) =>
                    setEditingCourse((prev) =>
                      prev
                        ? {
                            ...prev,
                            translations: prev.translations.map((t) =>
                              t.locale === 'ru'
                                ? { ...t, name: e.target.value }
                                : t,
                            ),
                          }
                        : null,
                    )
                  }
                  placeholder="Введите название курса на русском"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Course Name (EN)*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    editingCourse.translations.find((t) => t.locale === 'en')
                      ?.name || ''
                  }
                  onChange={(e) =>
                    setEditingCourse((prev) =>
                      prev
                        ? {
                            ...prev,
                            translations: prev.translations.map((t) =>
                              t.locale === 'en'
                                ? { ...t, name: e.target.value }
                                : t,
                            ),
                          }
                        : null,
                    )
                  }
                  placeholder="Enter course name in English"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Slug суффикс (RU)</label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    editingCourse.translations.find((t) => t.locale === 'ru')
                      ?.slug_suffix || ''
                  }
                  onChange={(e) =>
                    setEditingCourse((prev) =>
                      prev
                        ? {
                            ...prev,
                            translations: prev.translations.map((t) =>
                              t.locale === 'ru'
                                ? { ...t, slug_suffix: e.target.value }
                                : t,
                            ),
                          }
                        : null,
                    )
                  }
                  placeholder="course-name-ru"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Slug suffix (EN)</label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    editingCourse.translations.find((t) => t.locale === 'en')
                      ?.slug_suffix || ''
                  }
                  onChange={(e) =>
                    setEditingCourse((prev) =>
                      prev
                        ? {
                            ...prev,
                            translations: prev.translations.map((t) =>
                              t.locale === 'en'
                                ? { ...t, slug_suffix: e.target.value }
                                : t,
                            ),
                          }
                        : null,
                    )
                  }
                  placeholder="course-name-en"
                />
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  appearance="link"
                  onClick={() => setEditingCourse(null)}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  appearance="auth"
                  onClick={handleEditCourse}
                  disabled={
                    !editingCourse.translations.find((t) => t.locale === 'ru')
                      ?.name ||
                    !editingCourse.translations.find((t) => t.locale === 'en')
                      ?.name ||
                    editLoading
                  }
                >
                  {editLoading ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  },
);
